import { PASSENGER_IMAGE, WHATSAPP_PASSENGERS_GROUP, WHATSAPP_LINK } from "../../lib/troy";
import {
  UserPlus,
  MessageSquare,
  Car,
  Sticker,
  Check,
  ExternalLink,
  MessageCircle,
} from "lucide-react";

const STEPS = [
  {
    n: "01",
    icon: UserPlus,
    title: "Entre no grupo de passageiros da Troy",
    desc: "Em poucos segundos, você entra no nosso grupo oficial no WhatsApp. Sem app, sem cadastro chato.",
  },
  {
    n: "02",
    icon: MessageSquare,
    title: "Solicite sua corrida no grupo",
    desc: "Mande a localização e o destino direto no grupo. Digitou, enviou — pronto.",
  },
  {
    n: "03",
    icon: Car,
    title: "Um motorista disponível responde rápido",
    desc: "O motorista mais próximo confirma a corrida em poucos segundos, direto com você.",
  },
  {
    n: "04",
    icon: Sticker,
    title: "Identifique o carro pela figurinha",
    desc: "O motorista envia uma figurinha com a foto do carro. Você reconhece na hora e embarca com segurança.",
  },
];

const ADVANTAGES = [
  "Rapidez: resposta em segundos no grupo",
  "Fácil de usar: é só mandar mensagem",
  "Comunicação direta com os motoristas",
  "Sem filas de algoritmo, sem surge/dinâmica",
  "Motoristas conhecidos da região",
];

export default function Passengers() {
  return (
    <section
      id="passageiros"
      data-testid="passengers-section"
      className="relative py-24 md:py-32 border-t border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-5 lg:sticky lg:top-28">
          <div className="relative rounded-3xl overflow-hidden border border-[#FFCC00]/15 aspect-[4/5]">
            <img
              src={PASSENGER_IMAGE}
              alt="Passageira usando smartphone no banco traseiro"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
            <div className="absolute left-4 top-4 px-3 py-1.5 rounded-full bg-[#FFCC00] text-black text-[10px] font-black tracking-widest">
              PARA PASSAGEIROS
            </div>
            <div className="absolute left-5 right-5 bottom-5">
              <p className="font-display text-2xl md:text-3xl font-black leading-tight">
                “Chamei no grupo, em 3 minutos o motorista já tava na porta.”
              </p>
              <p className="mt-2 text-white/60 text-sm">— Camila, General Carneiro</p>
            </div>
          </div>

          <a
            href={WHATSAPP_PASSENGERS_GROUP}
            target="_blank"
            rel="noreferrer noopener"
            data-testid="passengers-group-cta-side"
            className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] text-white font-bold py-4 px-6 rounded-full hover:brightness-110 transition-all transform hover:-translate-y-0.5 shadow-[0_0_32px_rgba(37,211,102,0.35)]"
          >
            Entrar no grupo e pedir corrida agora
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>

        <div className="lg:col-span-7">
          <p className="text-xs tracking-[0.25em] uppercase text-[#FFCC00] font-bold mb-3">
            Como funciona
          </p>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-tight mb-5">
            Pedir uma Troy é{" "}
            <span className="text-[#FFCC00]">absurdamente simples</span>.
          </h2>
          <p className="text-white/70 text-lg leading-relaxed mb-8 max-w-2xl">
            Tudo acontece dentro do{" "}
            <strong className="text-white">grupo oficial de passageiros</strong>{" "}
            da Troy no WhatsApp. Você pede, o motorista responde, e a corrida sai
            na hora.
          </p>

          <div className="space-y-4">
            {STEPS.map((s) => (
              <div
                key={s.n}
                data-testid={`passenger-step-${s.n}`}
                className="flex gap-5 p-6 rounded-2xl bg-[#18181C] border border-[#FFCC00]/15 hover:border-[#FFCC00]/40 transition-colors"
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#FFCC00]/10 border border-[#FFCC00]/30 text-[#FFCC00] font-display font-black flex items-center justify-center">
                  {s.n}
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <s.icon className="w-4 h-4 text-[#FFCC00]" />
                    <h3 className="font-display text-xl font-bold">{s.title}</h3>
                  </div>
                  <p className="text-white/65 leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Sticker highlight */}
          <div
            data-testid="sticker-highlight"
            className="mt-6 p-6 rounded-2xl bg-gradient-to-br from-[#FF3B30]/15 via-transparent to-transparent border border-[#FF3B30]/30 flex items-start gap-4"
          >
            <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#FF3B30]/20 border border-[#FF3B30]/40 text-[#FF3B30] flex items-center justify-center">
              <Sticker className="w-5 h-5" />
            </div>
            <div>
              <h4 className="font-display text-lg font-bold mb-1">
                Identificação por figurinha
              </h4>
              <p className="text-white/70 leading-relaxed">
                O motorista disponível envia uma{" "}
                <strong className="text-white">figurinha com a foto do carro</strong>{" "}
                no grupo. Assim você identifica o veículo na hora e o atendimento
                fica ainda mais rápido e seguro.
              </p>
            </div>
          </div>

          <div className="mt-8 p-7 rounded-2xl bg-gradient-to-br from-[#FFCC00]/10 to-transparent border border-[#FFCC00]/30">
            <h3 className="font-display text-2xl font-bold mb-4">
              Por que passageiros escolhem a Troy
            </h3>
            <ul className="grid sm:grid-cols-2 gap-3">
              {ADVANTAGES.map((a) => (
                <li
                  key={a}
                  className="flex items-start gap-3 text-white/85"
                  data-testid={`passenger-advantage-${a.slice(0, 10)}`}
                >
                  <Check className="w-5 h-5 text-[#FFCC00] flex-shrink-0 mt-0.5" />
                  <span>{a}</span>
                </li>
              ))}
            </ul>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <a
                href={WHATSAPP_PASSENGERS_GROUP}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="passengers-group-cta"
                className="inline-flex items-center justify-center gap-2 bg-[#FFCC00] text-black font-bold py-3 px-6 rounded-full hover:bg-[#E6B800] transition-all transform hover:-translate-y-0.5"
              >
                <ExternalLink className="w-4 h-4" />
                Entrar no grupo e pedir corrida agora
              </a>
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noreferrer noopener"
                data-testid="passengers-whatsapp-direct"
                className="inline-flex items-center justify-center gap-2 bg-white/5 border border-white/15 text-white font-bold py-3 px-6 rounded-full hover:bg-white/10 transition-all"
              >
                <MessageCircle className="w-4 h-4" />
                Falar direto
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
